import React, { useState, useMemo, useRef } from "react";
import useAutoSave from "../hooks/useAutoSave";
import AutoSaveStatus from "./AutoSaveStatus";
import { useTheme } from "@mui/material/styles";
import {
  Dialog,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Divider,
  Tooltip,
  FormControlLabel,
  Switch,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { moveStep } from "./skillSheetSections";

// ── Design tokens — matches Skill Sheet Detail page / demo brand system ───────
const C = {
  ink: "var(--color-ink)",
  ink2: "var(--color-ink-2)",
  ink3: "var(--color-ink-3)",
  ink4: "rgba(31,22,51,0.4)",
  line: "var(--color-hairline-cloud)",
  surface: "#ffffff",
  surface2: "var(--color-surface-press-light)",
  surface3: "var(--color-surface-press-stronger)",
  danger: "var(--color-danger)",
  danger50: "var(--color-danger-bg)",
  live: "#c23d68",
  live14: "var(--color-pink-14)",
  green: "var(--color-success)",
  green18: "var(--color-lime-18)",
  amber: "var(--color-warn)",
  amber50: "var(--color-warn-bg)",
};

let _uid = 0;
const uid = () => `id_${++_uid}_${Date.now()}`;

function makeSection(title = "") {
  return { id: uid(), title, items: [] };
}

function makeItem() {
  return {
    id: uid(),
    criteria: "",
    validationType: "Pass/Fail",
    isCritical: false,
    pointValue: 1,
  };
}

function parseTagsRaw(raw = "[]") {
  try { const v = JSON.parse(raw); return Array.isArray(v) ? v : []; } catch { return []; }
}

function hydrateInitial(initial) {
  if (!initial) {
    return {
      title: "",
      description: "",
      passingScore: "",
      timeLimitMin: "",
      scenarioContext: "",
      equipment: "",
      changeNote: "",
      published: true,
      peerAssessmentRequired: true,
      category: "",
      existingTags: [],
      sections: [makeSection("Evaluation Checklist")],
    };
  }

  const items = initial.items || [];
  const sectionMap = new Map();
  const unsectioned = [];

  items.forEach((item) => {
    const { pointsAwarded: legacyPts, ...rest } = item;
    const withId = { ...rest, id: item.id ?? uid(), pointValue: item.pointValue ?? legacyPts ?? 1 };
    const key = withId.sectionTitle || "";
    if (!key) {
      unsectioned.push(withId);
    } else {
      if (!sectionMap.has(key)) sectionMap.set(key, []);
      sectionMap.get(key).push(withId);
    }
  });

  const sections = [];
  if (unsectioned.length > 0) {
    sections.push({ id: uid(), title: "Evaluation Checklist", items: unsectioned });
  }
  sectionMap.forEach((sItems, title) => {
    sections.push({ id: uid(), title, items: sItems });
  });

  if (sections.length === 0) sections.push(makeSection("Evaluation Checklist"));

  return {
    title: initial.title || "",
    description: initial.description || "",
    passingScore: initial.passingScore ?? "",
    timeLimitMin: initial.timeLimitMin ?? "",
    scenarioContext: initial.scenarioContext || "",
    equipment: initial.equipment || "",
    changeNote: "",
    published: initial.status ? initial.status === "PUBLISHED" : initial.enabled !== false,
    peerAssessmentRequired: initial.peerAssessmentRequired !== false,
    category: initial.category || "",
    existingTags: parseTagsRaw(initial.tags),
    sections,
  };
}

function flattenSections(sections) {
  return sections.flatMap((s) =>
    s.items.map((item) => ({
      ...item,
      sectionTitle: s.title || "Evaluation Checklist",
    }))
  );
}

// ─── Small primitives ─────────────────────────────────────────────────────────
function Ico({ size = 15, sw = 2, children, style }) {
  return <Box component="svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" style={style}>{children}</Box>;
}

function StatTile({ icon, iconBg, iconColor, value, label }) {
  return (
    <Box sx={{ flex: 1, minWidth: 0, bgcolor: C.surface, border: `1px solid ${C.line}`, borderRadius: "12px", p: "12px 14px", display: "flex", alignItems: "center", gap: "11px" }}>
      <Box sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: iconBg, color: iconColor, display: "grid", placeItems: "center", flexShrink: 0 }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ fontSize: "20px", fontWeight: 700, letterSpacing: "-.02em", lineHeight: 1, color: C.ink, fontFamily: "var(--font-display)" }}>{value}</Box>
        <Box sx={{ fontSize: "10.5px", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: C.ink3, mt: "3px" }}>{label}</Box>
      </Box>
    </Box>
  );
}

// ─── Section Editor ───────────────────────────────────────────────────────────
function SectionEditor({
  section,
  onUpdate,
  onDelete,
  isOnly,
  collapsed,
  onToggleCollapse,
  dragRef,
  onMoveStep,
  sectionIndex,
  setDragOverSectionIdx,
}) {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const [newCriteria, setNewCriteria] = useState("");
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const endTestId = section.title
    ? `skill-section-end-${section.title}`
    : `skill-section-end-untitled-${section.id}`;

  const dropItem = (e, toIndex, { expandIfCollapsed = false } = {}) => {
    if (dragRef.current?.kind !== "item") return;
    e.preventDefault();
    e.stopPropagation();
    const { sectionId, itemId } = dragRef.current;
    onMoveStep(sectionId, itemId, section.id, toIndex);
    dragRef.current = null;
    setDragOverIdx(null);
    if (expandIfCollapsed && collapsed) onToggleCollapse();
  };

  const updateItem = (itemId, patch) => {
    onUpdate({
      ...section,
      items: section.items.map((i) => (i.id === itemId ? { ...i, ...patch } : i)),
    });
  };

  const addItem = () => {
    if (!newCriteria.trim()) return;
    const item = { ...makeItem(), criteria: newCriteria.trim() };
    onUpdate({ ...section, items: [...section.items, item] });
    setNewCriteria("");
  };

  const removeItem = (itemId) => {
    onUpdate({ ...section, items: section.items.filter((i) => i.id !== itemId) });
  };

  return (
    <Box sx={{ border: `1px solid ${C.line}`, borderRadius: "12px", mb: "14px", overflow: "hidden", bgcolor: C.surface }}>
      <Box
        onDragOver={(e) => {
          if (dragRef.current?.kind !== "item") return;
          e.preventDefault();
          e.stopPropagation();
        }}
        onDrop={(e) => dropItem(e, section.items.length, { expandIfCollapsed: true })}
        sx={{ display: "flex", alignItems: "center", gap: 1, px: "14px", py: "11px", bgcolor: C.surface2, borderBottom: collapsed ? "none" : `1px solid ${C.line}` }}
      >
        <Box
          component="button"
          draggable
          onClick={onToggleCollapse}
          onDragStart={(e) => {
            e.stopPropagation();
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("text/plain", `section:${section.id}`);
            dragRef.current = { kind: "section", index: sectionIndex };
          }}
          onDragEnd={() => { dragRef.current = null; setDragOverSectionIdx(null); }}
          sx={{ p: 0, border: 0, bgcolor: "transparent", cursor: "grab", color: C.ink3, display: "flex", flexShrink: 0 }}
        >
          <DragIndicatorIcon sx={{ fontSize: 18 }} />
        </Box>
        <TextField
          value={section.title}
          onChange={(e) => onUpdate({ ...section, title: e.target.value })}
          placeholder="Section name…"
          variant="standard"
          size="small"
          sx={{
            flex: 1,
            "& .MuiInput-root": { fontWeight: 700, fontSize: "0.95rem", color: C.ink, fontFamily: "var(--font-display)" },
            "& .MuiInput-underline:before": { borderBottomColor: "transparent" },
            "& .MuiInput-underline:hover:before": { borderBottomColor: C.line },
          }}
          inputProps={{ style: { padding: "2px 0" } }}
        />
        <Box sx={{ fontSize: "11px", fontWeight: 700, px: "9px", py: "3px", borderRadius: "999px", bgcolor: C.surface3, color: C.ink2, flexShrink: 0, whiteSpace: "nowrap" }}>
          {section.items.length} item{section.items.length !== 1 ? "s" : ""}
        </Box>
        <Box component="button" onClick={onToggleCollapse} title={collapsed ? "Expand" : "Collapse"}
          sx={{ width: 26, height: 26, borderRadius: "7px", border: 0, bgcolor: "transparent", color: C.ink3, cursor: "pointer", display: "grid", placeItems: "center", flexShrink: 0, transition: "transform .15s", transform: collapsed ? "rotate(-90deg)" : "none", "&:hover": { bgcolor: C.surface3 } }}>
          <Ico size={14} sw={2.3}><polyline points="6 9 12 15 18 9" /></Ico>
        </Box>
        {!isOnly && (
          <Tooltip title="Remove section">
            <IconButton size="small" onClick={onDelete} sx={{ color: C.ink3, "&:hover": { color: C.danger, bgcolor: C.danger50 } }}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {!collapsed && (
        <Box sx={{ px: "14px", pt: "10px", pb: "10px" }}>
          {section.items.length === 0 && (
            <Typography
              onDragOver={(e) => {
                if (dragRef.current?.kind !== "item") return;
                e.preventDefault();
                e.stopPropagation();
              }}
              onDrop={(e) => dropItem(e, 0)}
              sx={{ color: C.ink4, fontStyle: "italic", mb: 1, fontSize: "0.82rem" }}
            >
              No items yet. Add performance criteria below.
            </Typography>
          )}

          {section.items.map((item, idx) => (
            <Box
              key={item.id}
              draggable
              data-testid={`skill-step-${item.id}`}
              onDragStart={(e) => {
                e.stopPropagation();
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("text/plain", String(item.id));
                dragRef.current = { kind: "item", sectionId: section.id, itemId: item.id };
              }}
              onDragOver={(e) => {
                if (dragRef.current?.kind !== "item") return;
                e.preventDefault();
                e.stopPropagation();
                setDragOverIdx(idx);
              }}
              onDrop={(e) => dropItem(e, idx)}
              onDragEnd={() => {
                dragRef.current = null;
                setDragOverIdx(null);
              }}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                p: "9px 10px",
                mb: "3px",
                borderRadius: "9px",
                bgcolor: item.isCritical ? C.danger50 : "transparent",
                borderLeft: item.isCritical ? `2.5px solid ${C.danger}` : "2.5px solid transparent",
                outline: dragOverIdx === idx ? `2px solid ${C.ink}` : "none",
                cursor: "grab",
                transition: "background .12s",
                "&:hover": { bgcolor: item.isCritical ? C.danger50 : C.surface2 },
              }}
            >
              <DragIndicatorIcon sx={{ fontSize: 16, color: C.ink3, flexShrink: 0, mt: "4px", opacity: 0.5, "&:hover": { opacity: 1 } }} />
              <Box sx={{ flex: 1 }}>
                <TextField
                  value={item.criteria}
                  onChange={(e) => updateItem(item.id, { criteria: e.target.value })}
                  placeholder="Performance criteria…"
                  variant="standard"
                  size="small"
                  fullWidth
                  multiline
                  sx={{
                    "& .MuiInput-root": { fontSize: "0.85rem", color: C.ink },
                    "& .MuiInput-underline:before": { borderBottomColor: "transparent" },
                    "& .MuiInput-underline:hover:before": { borderBottomColor: C.line },
                  }}
                />
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: "5px", flexShrink: 0, mt: "1px" }}>
                <Box
                  component="button"
                  onClick={() => updateItem(item.id, { validationType: item.validationType === "Pass/Fail" ? "Observation" : "Pass/Fail" })}
                  title="Toggle validation type"
                  sx={{ height: 26, px: "10px", borderRadius: "999px", border: 0, cursor: "pointer", fontFamily: "inherit", fontSize: "11px", fontWeight: 700, bgcolor: C.surface3, color: C.ink2, "&:hover": { bgcolor: C.surface2 } }}
                >
                  {item.validationType}
                </Box>

                <Tooltip title={item.isCritical ? "Remove critical flag" : "Mark as critical (automatic fail if missed)"}>
                  <Box
                    component="button"
                    onClick={() => updateItem(item.id, { isCritical: !item.isCritical })}
                    sx={{ width: 26, height: 26, borderRadius: "7px", border: 0, cursor: "pointer", display: "grid", placeItems: "center", bgcolor: item.isCritical ? C.amber50 : "transparent", color: item.isCritical ? C.amber : C.ink4, "&:hover": { bgcolor: item.isCritical ? C.amber50 : C.surface3 } }}
                  >
                    <WarningAmberIcon sx={{ fontSize: 16 }} />
                  </Box>
                </Tooltip>

                <Box sx={{ display: "flex", alignItems: "center", gap: "1px" }}>
                  <IconButton size="small" disabled={item.pointValue <= 0} onClick={() => updateItem(item.id, { pointValue: Math.max(0, item.pointValue - 1) })} sx={{ color: C.ink3, width: 22, height: 22, fontSize: "1rem" }}>−</IconButton>
                  <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 16, textAlign: "center", color: C.ink2 }}>{item.pointValue}</Typography>
                  <IconButton size="small" onClick={() => updateItem(item.id, { pointValue: item.pointValue + 1 })} sx={{ color: C.ink3, width: 22, height: 22, fontSize: "1rem" }}>+</IconButton>
                </Box>

                <Tooltip title="Remove item">
                  <IconButton size="small" onClick={() => removeItem(item.id)} sx={{ color: C.ink3, "&:hover": { color: C.danger, bgcolor: C.danger50 } }}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          ))}

          <Box
            data-testid={endTestId}
            onDragOver={(e) => {
              if (dragRef.current?.kind !== "item") return;
              e.preventDefault();
              e.stopPropagation();
            }}
            onDrop={(e) => dropItem(e, section.items.length)}
            sx={{ minHeight: 12 }}
          />

          <Box sx={{ display: "flex", gap: 1, mt: "10px" }}>
            <TextField
              value={newCriteria}
              onChange={(e) => setNewCriteria(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addItem())}
              placeholder="Add performance criteria…"
              variant="outlined"
              size="small"
              fullWidth
              sx={{ "& .MuiOutlinedInput-root": { fontSize: "0.83rem", borderRadius: "8px", bgcolor: "#fff" } }}
            />
            <Button onClick={addItem} variant="outlined" size="small"
              sx={{ borderColor: tPrimary, color: tPrimary, textTransform: "none", fontWeight: 700, borderRadius: "8px", whiteSpace: "nowrap", flexShrink: 0 }}>
              + Add
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ─── Live Preview — mirrors the printed / student-facing skill sheet ─────────
function LivePreview({ form, stats }) {
  const sectionsWithItems = form.sections.filter((s) => s.items.length > 0);
  const passPts = form.passingScore === "" ? null : Number(form.passingScore);

  return (
    <Box sx={{ bgcolor: C.surface, border: `1px solid ${C.line}`, borderRadius: "14px", p: "22px 24px", minHeight: 400 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: "12px", color: C.ink3, mb: "18px" }}>
        <Box>Student Name: <Box component="span" sx={{ display: "inline-block", width: 150, borderBottom: `1px solid ${C.line}`, ml: "4px" }} /></Box>
        <Box>Date: <Box component="span" sx={{ display: "inline-block", width: 90, borderBottom: `1px solid ${C.line}`, ml: "4px" }} /></Box>
      </Box>

      <Box sx={{ fontSize: "11px", fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: C.ink3, mb: "4px" }}>Skill Sheet</Box>
      <Typography sx={{ fontSize: "22px", fontWeight: 700, letterSpacing: "-.01em", color: form.title ? C.ink : C.ink4, fontFamily: "var(--font-display)", lineHeight: 1.2 }}>
        {form.title || "Skill Sheet Title"}
      </Typography>
      {form.description && (
        <Typography sx={{ fontSize: "13px", color: C.ink3, mt: "6px", lineHeight: 1.55 }}>{form.description}</Typography>
      )}

      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "16px", mt: "13px", pb: "16px", borderBottom: `1px solid ${C.line}`, fontSize: "12.5px", color: C.ink2, fontWeight: 600 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}><Ico size={13} style={{ color: C.ink4 }}><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /></Ico>{stats.sectionCount} section{stats.sectionCount !== 1 ? "s" : ""}</Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}><Ico size={13} style={{ color: C.ink4 }}><line x1="4" y1="9" x2="20" y2="9" /><line x1="4" y1="15" x2="20" y2="15" /><line x1="10" y1="3" x2="8" y2="21" /><line x1="16" y1="3" x2="14" y2="21" /></Ico>{stats.totalPoints} pts total</Box>
        {passPts != null && <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}><Ico size={13} style={{ color: C.ink4 }}><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /></Ico>Pass {passPts} / {stats.totalPoints} pts</Box>}
        {form.timeLimitMin !== "" && <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}><Ico size={13} style={{ color: C.ink4 }}><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></Ico>{form.timeLimitMin} min</Box>}
      </Box>

      {sectionsWithItems.length === 0 ? (
        <Box sx={{ textAlign: "center", color: C.ink4, fontSize: "13.5px", py: "36px" }}>Add sections and criteria to see the preview.</Box>
      ) : sectionsWithItems.map((section, sIdx) => {
        const secPts = section.items.reduce((s, i) => s + (i.pointValue ?? 1), 0);
        return (
          <Box key={section.id} sx={{ mt: "18px" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: "8px" }}>
              <Typography sx={{ fontSize: "13.5px", fontWeight: 700, color: C.ink, fontFamily: "var(--font-display)" }}>
                {String(sIdx + 1).padStart(2, "0")}. {section.title || "Section"}
              </Typography>
              <Box sx={{ fontSize: "11.5px", fontWeight: 700, color: C.ink3 }}>{secPts} pts</Box>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "1px" }}>
              {section.items.map((item) => (
                <Box key={item.id} sx={{ display: "flex", alignItems: "flex-start", gap: "10px", py: "6px" }}>
                  <Box sx={{ width: 15, height: 15, borderRadius: "4px", border: `1.5px solid ${item.isCritical ? C.danger : C.line}`, flexShrink: 0, mt: "2px" }} />
                  <Box sx={{ flex: 1, fontSize: "13px", color: C.ink, lineHeight: 1.5 }}>
                    {item.criteria || <Box component="span" sx={{ color: C.ink4 }}>criteria text…</Box>}
                    {item.isCritical && (
                      <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "3px", fontSize: "10px", fontWeight: 700, letterSpacing: ".03em", textTransform: "uppercase", color: C.danger, bgcolor: C.danger50, px: "6px", py: "1px", borderRadius: "999px", ml: "7px", verticalAlign: "middle" }}>
                        <Ico size={8} sw={2.5}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /></Ico>Critical
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ fontSize: "11.5px", fontWeight: 600, color: C.ink4, flexShrink: 0 }}>{item.pointValue ?? 1} pt</Box>
                </Box>
              ))}
            </Box>
          </Box>
        );
      })}

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "20px", mt: "26px", pt: "18px", borderTop: `1px solid ${C.line}`, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", gap: "8px" }}>
          <Box sx={{ height: 32, px: "16px", borderRadius: "8px", border: `1.5px solid ${C.ink}`, display: "grid", placeItems: "center", fontSize: "11px", fontWeight: 700, letterSpacing: ".05em", color: C.ink }}>PASS</Box>
          <Box sx={{ height: 32, px: "16px", borderRadius: "8px", border: `1.5px solid ${C.line}`, display: "grid", placeItems: "center", fontSize: "11px", fontWeight: 700, letterSpacing: ".05em", color: C.ink3 }}>FAIL</Box>
        </Box>
        <Box sx={{ fontSize: "12px", color: C.ink3 }}>
          <Box>Instructor signature</Box>
          <Box sx={{ width: 160, borderBottom: `1px solid ${C.line}`, mt: "18px" }} />
        </Box>
      </Box>
      <Box sx={{ fontSize: "12px", color: C.ink3, mt: "10px" }}>
        Time started: __________ Ended: __________
      </Box>
    </Box>
  );
}

// ─── Main Modal ───────────────────────────────────────────────────────────────
/**
 * Auto-saves while the sheet is edited (see useAutoSave). Each time the editor opens it is mounted fresh, so what it
 * opened with is the saved baseline and only real edits are written.
 */
export default function SkillSheetBuilderModal(props) {
  return <SkillSheetBuilderDialog key={props.open ? "open" : "closed"} {...props} />;
}

function SkillSheetBuilderDialog({ open, onClose, onSave, initial, bankMode = false }) {
  const [form, setForm] = useState(() => hydrateInitial(initial));
  const savedIdRef = useRef(initial?.id ?? null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [collapsedSections, setCollapsedSections] = useState(new Set());
  const dragRef = React.useRef(null);
  const [dragOverSectionIdx, setDragOverSectionIdx] = useState(null);

  const reorderSections = (from, to) => {
    if (from === to) return;
    setForm(f => {
      const next = [...f.sections];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return { ...f, sections: next };
    });
  };

  const moveItem = (fromSectionId, itemId, toSectionId, toIndex) => {
    setForm((current) => {
      const sections = moveStep(current.sections, fromSectionId, itemId, toSectionId, toIndex);
      return sections === current.sections ? current : { ...current, sections };
    });
  };

  // The editor is mounted fresh each time it opens (see SkillSheetBuilderModal), so its state starts from `initial`.
  // Nothing re-hydrates it later: a parent refreshing `initial` after an auto-save must not overwrite what is being typed.

  const stats = useMemo(() => {
    const allItems = form.sections.flatMap((s) => s.items);
    const totalItems = allItems.length;
    const totalPoints = allItems.reduce((s, i) => s + (i.pointValue ?? 1), 0);
    const criticalCount = allItems.filter((i) => i.isCritical).length;
    return { totalItems, totalPoints, criticalCount, sectionCount: form.sections.length };
  }, [form.sections]);

  const updateSection = (updated) => {
    setForm((f) => ({
      ...f,
      sections: f.sections.map((s) => (s.id === updated.id ? updated : s)),
    }));
  };

  const deleteSection = (sectionId) => {
    setForm((f) => ({
      ...f,
      sections: f.sections.filter((s) => s.id !== sectionId),
    }));
  };

  const addSection = () => {
    setForm((f) => ({ ...f, sections: [...f.sections, makeSection("")] }));
  };

  const toggleCollapse = (sectionId) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      next.has(sectionId) ? next.delete(sectionId) : next.add(sectionId);
      return next;
    });
  };

  const allCollapsed = form.sections.length > 0 && form.sections.every((s) => collapsedSections.has(s.id));
  const setAllCollapsed = (collapse) => {
    setCollapsedSections(collapse ? new Set(form.sections.map((s) => s.id)) : new Set());
  };

  const handlePrintPreview = () => {
    const win = window.open("", "_blank");
    if (!win) return;
    const sectionsWithItems = form.sections.filter((s) => s.items.length > 0);
    const body = sectionsWithItems.map((sec, i) => `
      <h3>${String(i + 1).padStart(2, "0")}. ${sec.title || "Section"}</h3>
      <table><thead><tr><th>#</th><th>Criteria</th><th>Pass</th><th>Fail</th><th>Pts</th></tr></thead><tbody>
      ${sec.items.map((item, idx) => `
        <tr>
          <td>${idx + 1}</td>
          <td>${item.isCritical ? "<b style=\"color:#c23d68\">CRIT</b> " : ""}${item.criteria || ""}</td>
          <td style="text-align:center">☐</td><td style="text-align:center">☐</td>
          <td style="text-align:center">${item.pointValue ?? 1}</td>
        </tr>`).join("")}
      </tbody></table>`).join("");
    win.document.write(`<!doctype html><html><head><title>${form.title}</title>
      <style>body{font-family:Arial,sans-serif;margin:24px;color:#111}h1{font-size:18px;margin-bottom:2px}
      h3{font-size:13px;text-transform:uppercase;letter-spacing:.05em;margin:18px 0 6px}
      table{width:100%;border-collapse:collapse}th,td{padding:6px 8px;border-bottom:1px solid #e5e7eb;font-size:13px;text-align:left}
      th{text-transform:uppercase;font-size:11px;color:#666}</style></head><body>
      <h1>${form.title || "Skill Sheet"}</h1>
      <p>${form.description || ""}</p>${body}</body></html>`);
    win.document.close();
    win.print();
  };

  const handleSave = async (action = "save") => {
    if (!form.title.trim()) return;
    setSaving(true);
    setSaveError("");
    try {
      // An auto-save still running must finish first, so this save updates that copy instead of creating another.
      await autosave.settle();
      await onSave({
        title: form.title.trim(),
        description: form.description.trim(),
        passingScore: form.passingScore === "" ? null : Number(form.passingScore),
        timeLimitMin: form.timeLimitMin === "" ? null : Number(form.timeLimitMin),
        scenarioContext: form.scenarioContext.trim(),
        equipment: form.equipment.trim(),
        items: flattenSections(form.sections),
        tags: JSON.stringify(form.existingTags || []),
        enabled: form.published,
        peerAssessmentRequired: form.peerAssessmentRequired,
        category: form.category.trim() || null,
        // Always the id of the item this editor has saved, so a later Save updates it instead of creating a copy.
        savedBankId: savedIdRef.current,
        ...(bankMode ? { requestedStatus: action === "publish" ? "PUBLISHED" : initial?.status || "DRAFT" } : {}),
        ...(initial ? { changeNote: form.changeNote.trim() } : {}),
      });
      onClose();
    } catch (e) {
      if (e.savedBankId) savedIdRef.current = e.savedBankId;
      setSaveError(e?.response?.data?.error || e?.response?.data?.message || e?.message || "Save failed. Please try again.");
      setSaving(false);
    }
  };

  const isEdit = Boolean(initial);
  const canSave = form.title.trim().length > 0;
  const published = initial?.status === "PUBLISHED";

  // The content that auto-save writes. Identifiers and status are added at save time, so they never count as edits.
  const contentPayload = () => ({
    title: form.title.trim(),
    description: form.description.trim(),
    passingScore: form.passingScore === "" ? null : Number(form.passingScore),
    timeLimitMin: form.timeLimitMin === "" ? null : Number(form.timeLimitMin),
    scenarioContext: form.scenarioContext.trim(),
    equipment: form.equipment.trim(),
    items: flattenSections(form.sections),
    tags: JSON.stringify(form.existingTags || []),
    enabled: form.published,
    peerAssessmentRequired: form.peerAssessmentRequired,
    category: form.category.trim() || null,
    ...(initial ? { changeNote: form.changeNote.trim() } : {}),
  });

  const autosave = useAutoSave({
    enabled: open && canSave && !published,
    data: contentPayload(),
    save: async (content) => {
      const result = await onSave({
        ...content,
        autosave: true,
        savedBankId: savedIdRef.current,
        requestedStatus: initial?.status || "DRAFT",
      });
      if (result?.id) savedIdRef.current = result.id;
    },
  });

  // Closing never waits: a pending change still saves in the background.
  const closeEditor = () => {
    autosave.flush();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "min(1240px, 96vw)",
          height: "min(90vh, 900px)",
          borderRadius: "16px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          bgcolor: C.surface,
        },
      }}
    >
      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: "13px", px: "22px", py: "16px", borderBottom: `1px solid ${C.line}`, flexShrink: 0 }}>
        <Box sx={{ width: 38, height: 38, borderRadius: "10px", bgcolor: C.surface2, color: C.ink2, display: "grid", placeItems: "center", flexShrink: 0 }}>
          <Ico size={17}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></Ico>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "17px", color: C.ink, fontFamily: "var(--font-display)", letterSpacing: "-.01em" }}>
            {isEdit ? "Edit Skill Sheet" : "Build Skill Sheet"}
          </Typography>
          <Typography sx={{ fontSize: "12.5px", color: C.ink3, mt: "2px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {form.title || "Define performance criteria, sections, and passing requirements"}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
          <Button onClick={handlePrintPreview} startIcon={<PrintOutlinedIcon sx={{ fontSize: 16 }} />}
            sx={{ height: 34, px: "12px", borderRadius: "8px", fontSize: "12.5px", fontWeight: 600, color: C.ink2, textTransform: "none", "&:hover": { bgcolor: C.surface2 } }}>
            Print preview
          </Button>
          {!bankMode && <Tooltip title="Click to toggle Published / Draft">
            <Box component="button" onClick={() => setForm((f) => ({ ...f, published: !f.published }))}
              sx={{ display: "inline-flex", alignItems: "center", gap: "6px", height: 30, px: "12px", borderRadius: "999px", border: 0, cursor: "pointer", fontSize: "11.5px", fontWeight: 700, fontFamily: "inherit",
                bgcolor: form.published ? C.green18 : C.surface3, color: form.published ? "var(--color-trend-up)" : C.ink3 }}>
              <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "currentColor" }} />
              {form.published ? "Published" : "Draft"}
            </Box>
          </Tooltip>}
          <IconButton onClick={onClose} size="small" sx={{ color: C.ink3 }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* ── Stats Strip ── */}
      <Box sx={{ display: "flex", gap: "12px", px: "22px", py: "14px", bgcolor: C.surface2, borderBottom: `1px solid ${C.line}`, flexShrink: 0 }}>
        <StatTile icon={<Ico size={16}><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /></Ico>} iconBg="var(--color-primary-10)" iconColor="var(--color-primary)" value={stats.sectionCount} label="Sections" />
        <StatTile icon={<Ico size={16}><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></Ico>} iconBg="rgba(106,95,193,.12)" iconColor="var(--color-accent-violet)" value={stats.totalItems} label="Items" />
        <StatTile icon={<Ico size={16}><line x1="4" y1="9" x2="20" y2="9" /><line x1="4" y1="15" x2="20" y2="15" /><line x1="10" y1="3" x2="8" y2="21" /><line x1="16" y1="3" x2="14" y2="21" /></Ico>} iconBg={C.amber50} iconColor={C.amber} value={stats.totalPoints} label="Total Points" />
        <StatTile icon={<Ico size={16}><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></Ico>} iconBg={C.live14} iconColor={C.live} value={form.timeLimitMin !== "" ? `${form.timeLimitMin} min` : "—"} label="Time Limit" />
      </Box>

      {saveError && <Box sx={{ px: "22px", pt: "10px", color: C.danger, fontSize: "13px", flexShrink: 0 }}>{saveError}</Box>}

      {/* ── Body ── */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", flex: 1, overflow: "hidden" }}>
        {/* Left — Builder */}
        <Box sx={{ overflowY: "auto", px: "22px", py: "20px", borderRight: `1px solid ${C.line}` }}>
          {/* Basics */}
          <Typography sx={{ fontSize: "11px", fontWeight: 700, color: C.ink3, textTransform: "uppercase", letterSpacing: ".08em", mb: "12px" }}>
            Basics
          </Typography>
          <TextField
            label={<>Checklist title <Box component="span" sx={{ color: C.danger }}>*</Box></>}
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            required
            fullWidth
            size="small"
            variant="outlined"
            sx={{ mb: "14px" }}
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            fullWidth
            size="small"
            variant="outlined"
            multiline
            minRows={2}
            sx={{ mb: "14px" }}
          />
          <Box sx={{ display: "flex", gap: "14px", mb: "6px" }}>
            <TextField
              label="Points needed to pass"
              type="number"
              value={form.passingScore}
              onChange={(e) => setForm((f) => ({ ...f, passingScore: e.target.value === "" ? "" : e.target.value }))}
              size="small"
              variant="outlined"
              fullWidth
              inputProps={{ min: 0 }}
              InputProps={{ endAdornment: <Box sx={{ color: C.ink3, fontSize: "13px" }}>pts</Box> }}
            />
            <TextField
              label="Time limit"
              type="number"
              value={form.timeLimitMin}
              onChange={(e) => setForm((f) => ({ ...f, timeLimitMin: e.target.value === "" ? "" : e.target.value }))}
              size="small"
              variant="outlined"
              fullWidth
              inputProps={{ min: 0 }}
              InputProps={{ endAdornment: <Box sx={{ color: C.ink3, fontSize: "13px" }}>min</Box> }}
            />
          </Box>
          <FormControlLabel control={<Switch checked={form.peerAssessmentRequired} onChange={(e) => setForm((f) => ({ ...f, peerAssessmentRequired: e.target.checked }))} />} label="Require peer assessment before instructor sign-off" />
          <Typography sx={{ fontSize: 12, color: C.ink3, mb: 1 }}>Turn off for instructor-only testing.</Typography>
          <TextField label="Category (optional)" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} fullWidth size="small" sx={{ my: 1 }} />
          <TextField
            label="Scenario context (optional)"
            placeholder="Describe the scenario or setup given to the candidate…"
            value={form.scenarioContext}
            onChange={(e) => setForm((f) => ({ ...f, scenarioContext: e.target.value }))}
            fullWidth
            size="small"
            variant="outlined"
            multiline
            minRows={2}
            sx={{ mt: "8px" }}
          />
          <TextField
            label="Equipment (optional)"
            placeholder="List equipment or supplies needed for this skill check…"
            value={form.equipment}
            onChange={(e) => setForm((f) => ({ ...f, equipment: e.target.value }))}
            fullWidth
            size="small"
            variant="outlined"
            multiline
            minRows={2}
            sx={{ mt: "14px" }}
          />
          {isEdit && (
            <TextField
              label="What changed? (optional)"
              placeholder="e.g. Reduced time limit to 60s"
              value={form.changeNote}
              onChange={(e) => setForm((f) => ({ ...f, changeNote: e.target.value }))}
              fullWidth
              size="small"
              variant="outlined"
              sx={{ mt: "14px" }}
            />
          )}

          <Divider sx={{ borderColor: C.line, my: "20px" }} />

          {/* Sections */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "12px" }}>
            <Typography sx={{ fontSize: "11px", fontWeight: 700, color: C.ink3, textTransform: "uppercase", letterSpacing: ".08em" }}>
              Checklist Sections · {stats.sectionCount}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <Box component="button" onClick={() => setAllCollapsed(false)} sx={{ p: 0, border: 0, bgcolor: "transparent", cursor: "pointer", fontSize: "12px", fontWeight: 600, color: allCollapsed ? "var(--color-accent-violet)" : C.ink3, "&:hover": { color: "var(--color-accent-violet)" } }}>
                Expand all
              </Box>
              <Box component="button" onClick={() => setAllCollapsed(true)} sx={{ p: 0, border: 0, bgcolor: "transparent", cursor: "pointer", fontSize: "12px", fontWeight: 600, color: !allCollapsed ? "var(--color-accent-violet)" : C.ink3, "&:hover": { color: "var(--color-accent-violet)" } }}>
                Collapse all
              </Box>
            </Box>
          </Box>

          {form.sections.map((section, sIdx) => (
            <Box
              key={section.id}
              data-testid={`skill-section-slot-${sIdx}`}
              onDragOver={(e) => {
                if (dragRef.current?.kind !== "section") return;
                e.preventDefault();
                setDragOverSectionIdx(sIdx);
              }}
              onDrop={(e) => {
                if (dragRef.current?.kind !== "section") return;
                e.preventDefault();
                reorderSections(dragRef.current.index, sIdx);
                dragRef.current = null;
                setDragOverSectionIdx(null);
              }}
              sx={{ outline: dragOverSectionIdx === sIdx ? `2px solid ${C.ink}` : "none", borderRadius: "12px" }}
            >
              <SectionEditor
                section={section}
                onUpdate={updateSection}
                onDelete={() => deleteSection(section.id)}
                isOnly={form.sections.length === 1}
                collapsed={collapsedSections.has(section.id)}
                onToggleCollapse={() => toggleCollapse(section.id)}
                dragRef={dragRef}
                onMoveStep={moveItem}
                sectionIndex={sIdx}
                setDragOverSectionIdx={setDragOverSectionIdx}
              />
            </Box>
          ))}

          <Button size="small" startIcon={<AddIcon sx={{ fontSize: 16 }} />} onClick={addSection}
            sx={{ color: C.ink2, border: `1px dashed ${C.line}`, textTransform: "none", fontWeight: 600, fontSize: "12.5px", borderRadius: "10px", width: "100%", py: "9px", "&:hover": { bgcolor: C.surface2, borderColor: "var(--color-hairline-cool)" } }}>
            Add Section
          </Button>
        </Box>

        {/* Right — Live Preview */}
        <Box sx={{ overflowY: "auto", px: "22px", py: "20px", bgcolor: C.surface2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "12px" }}>
            <Typography sx={{ fontSize: "11px", fontWeight: 700, color: C.ink3, textTransform: "uppercase", letterSpacing: ".08em" }}>
              Skill Sheet · Live Preview
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "11px", fontWeight: 700, color: "var(--color-trend-up)" }}>
              <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "currentColor" }} />
              Live
            </Box>
          </Box>
          <LivePreview form={form} stats={stats} />
        </Box>
      </Box>

      {/* ── Footer ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "14px", px: "22px", py: "14px", borderTop: `1px solid ${C.line}`, bgcolor: C.surface, flexShrink: 0 }}>
        <Typography sx={{ fontSize: "12.5px", color: C.ink3, fontWeight: 500 }}>
          {stats.totalItems === 0
            ? "Ready · add at least one item"
            : `Ready · ${stats.sectionCount} section${stats.sectionCount !== 1 ? "s" : ""}, ${stats.totalItems} item${stats.totalItems !== 1 ? "s" : ""}, ${stats.totalPoints} pts`}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <AutoSaveStatus status={autosave.status} savedAt={autosave.savedAt} error={autosave.error} published={published} />
          <Button onClick={closeEditor} disabled={saving} variant="outlined">
            Cancel
          </Button>
          {bankMode && initial?.status !== "PUBLISHED" && <Button onClick={() => handleSave("publish")} disabled={saving || !canSave} variant="outlined">Publish</Button>}
          <Button onClick={() => handleSave()} disabled={saving || !canSave} variant="contained">
            {saving ? "Saving…" : isEdit ? "Save Changes" : bankMode ? "Save Draft" : "Create Skill Sheet"}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
